import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/hooks/useAppStore';
import { track } from '@/utils/tracker';

interface ModelEntry {
  id: string;
  name: string;
  descKey: string;
  bgImage: string;
  tagKey: string;
  tagBg: string;
  tagColor: string;
}

const modelEntries: ModelEntry[] = [
  {
    id: 'gemini_nano',
    name: 'Nano Banana',
    descKey: 'home_nanoBanana_desc',
    bgImage: 'https://static.readdy.ai/image/feef753f1ed8344cc3f9a51076258ebd/3ce106d65c9d7ab17d6c438ebc8bec79.png',
    tagKey: 'tool_hot',
    tagBg: 'rgba(251,191,36,0.9)',
    tagColor: '#92400E',
  },
  {
    id: 'gpt_image_2',
    name: 'ChatGPT Image',
    descKey: 'home_chatgptImage_desc',
    bgImage: 'https://static.readdy.ai/image/feef753f1ed8344cc3f9a51076258ebd/c111e8634d12aebc8de39a4e8a4f3954.png',
    tagKey: 'home_modelEntry_new',
    tagBg: 'rgba(34,197,94,0.9)',
    tagColor: '#14532D',
  },
];

export default function ModelEntryCards() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAppStore();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 300);
    modelEntries.forEach((entry) => {
      track.homeEntryExposure({
        entry_id: `model_${entry.id}`,
        entry_name: entry.name,
        source: 'home_model_entries',
      });
    });
    return () => clearTimeout(timer);
  }, []);

  const handleClick = (modelId: string) => {
    const entry = modelEntries.find((item) => item.id === modelId);
    track.homeEntryClick({
      entry_id: `model_${modelId}`,
      entry_name: entry?.name ?? modelId,
      source: 'home_model_entries',
    });
    if (user) {
      navigate(`/generate?model=${modelId}`);
    } else {
      navigate('/login?redirect=/generate');
    }
  };

  return (
    <div className="px-4 mt-4">
      {/* Section title */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-900 tracking-tight">{t('home_modelEntry_title')}</h2>
        <button
          onClick={() => navigate('/generate')}
          className="flex items-center gap-0.5 text-xs text-gray-400 font-medium cursor-pointer active:scale-95 transition-transform whitespace-nowrap"
        >
          <span>{t('home_viewMore')}</span>
          <i className="ri-arrow-right-s-line text-gray-400" />
        </button>
      </div>

      {/* Two cards side by side */}
      <div className="grid grid-cols-2 gap-3">
        {modelEntries.map((entry, idx) => (
          <button
            key={entry.id}
            onClick={() => handleClick(entry.id)}
            className="relative w-full overflow-hidden rounded-2xl cursor-pointer active:scale-[0.96] transition-all duration-300 text-left"
            style={{
              aspectRatio: '3/4',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(16px)',
              transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${idx * 0.1}s`,
            }}
          >
            {/* Background image */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
              style={{ backgroundImage: `url(${entry.bgImage})` }}
            />

            {/* 底部渐变遮罩 */}
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0.75) 100%)',
              }}
            />

            {/* 右上角标签 */}
            <div className="absolute top-2 right-2">
              <span
                className="px-2 py-0.5 rounded-md text-[10px] font-bold"
                style={{
                  background: entry.tagBg,
                  color: entry.tagColor,
                }}
              >
                {t(entry.tagKey)}
              </span>
            </div>

            {/* 底部内容 */}
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <h3 className="text-sm font-bold text-white tracking-tight">
                {entry.name}
              </h3>
              <p className="text-[10px] text-white/60 mt-0.5 leading-snug">
                {t(entry.descKey)}
              </p>
            </div>

            {/* 右下角箭头 */}
            <div className="absolute bottom-3 right-3">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(255,255,255,0.9)',
                }}
              >
                <i className="ri-arrow-right-up-line text-gray-800 text-xs" />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
